import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Trash2, Plus, Ticket, Copy, CheckCircle, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  getCodes,
  deleteCode,
  createPatriarchCode,
  CodeData,
  formatDate,
} from '@/features/admin/services/adminService'
import { useAuth } from '@/features/auth/contexts/AuthContext'

export default function AdminCodes() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [codes, setCodes] = useState<CodeData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set())
  const [isDeleting, setIsDeleting] = useState(false)
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  // Invite patriarch
  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [isInviting, setIsInviting] = useState(false)
  const [inviteResult, setInviteResult] = useState<{ success: boolean; code?: string; error?: string } | null>(null)

  useEffect(() => {
    loadCodes()
  }, [])

  const loadCodes = async () => {
    setIsLoading(true)
    const data = await getCodes()
    setCodes(data)
    setIsLoading(false)
  }

  const handleDelete = async (code: string) => {
    if (!confirm('¿Eliminar este código?')) return
    await deleteCode(code)
    loadCodes()
  }

  const handleDeleteSelected = async () => {
    if (!confirm(`¿Eliminar ${selectedCodes.size} código(s)?`)) return
    setIsDeleting(true)
    for (const code of selectedCodes) {
      await deleteCode(code)
    }
    setSelectedCodes(new Set())
    loadCodes()
    setIsDeleting(false)
  }

  const handleCopy = async (code: string) => {
    await navigator.clipboard.writeText(code)
    setCopiedCode(code)
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const handleInvitePatriarch = async () => {
    if (!inviteEmail || !user?.uid || !user?.displayName) return
    setIsInviting(true)
    const result = await createPatriarchCode(inviteEmail, user.uid, user.displayName)
    setInviteResult(result)
    setIsInviting(false)
    if (result.success) {
      loadCodes()
      setInviteEmail('')
    }
  }

  const toggleSelect = (code: string) => {
    const newSelected = new Set(selectedCodes)
    if (newSelected.has(code)) {
      newSelected.delete(code)
    } else {
      newSelected.add(code)
    }
    setSelectedCodes(newSelected)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-casino-gold" />
      </div>
    )
  }

  const patriarchCodes = codes.filter((c) => c.isPatriarchCode)
  const referralCodes = codes.filter((c) => !c.isPatriarchCode)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-cinzel text-2xl text-casino-gold">{t('admin.codes')}</h1>
        <div className="flex gap-2">
          {selectedCodes.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteSelected}
              disabled={isDeleting}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar ({selectedCodes.size})
            </Button>
          )}
          <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
            <DialogTrigger asChild>
              <Button variant="gold" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                {t('admin.invitePatriarch')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('admin.invitePatriarch')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Email del nuevo Patriarca</Label>
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="email@ejemplo.com"
                  />
                </div>
                {inviteResult && (
                  <div className={`p-3 rounded ${inviteResult.success ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}>
                    {inviteResult.success ? (
                      <>
                        <p>Código creado: <strong>{inviteResult.code}</strong></p>
                        <p className="text-sm mt-1">Se envió un email con el código.</p>
                      </>
                    ) : (
                      <p>{inviteResult.error}</p>
                    )}
                  </div>
                )}
                <Button
                  variant="gold"
                  className="w-full"
                  onClick={handleInvitePatriarch}
                  disabled={isInviting || !inviteEmail}
                >
                  {isInviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Enviar Invitación
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Patriarch codes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-purple-400 flex items-center gap-2">
            <Ticket className="w-5 h-5" />
            Códigos de Patriarca ({patriarchCodes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {patriarchCodes.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hay códigos de patriarca</p>
          ) : (
            <div className="space-y-2">
              {patriarchCodes.map((code) => (
                <div
                  key={code.code}
                  className="flex items-center justify-between p-3 bg-purple-900/20 rounded-lg border border-purple-800/30"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedCodes.has(code.code)}
                      onChange={() => toggleSelect(code.code)}
                      className="rounded"
                    />
                    <div>
                      <p className="font-mono font-bold text-purple-300">{code.code}</p>
                      <p className="text-xs text-gray-500">
                        Por: {code.createdByName} - {formatDate(code.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {code.usedCount > 0 ? (
                      <span className="text-xs bg-zinc-700 text-gray-400 px-2 py-1 rounded">Usado</span>
                    ) : (
                      <span className="text-xs bg-green-900/50 text-green-400 px-2 py-1 rounded">Activo</span>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleCopy(code.code)}>
                      {copiedCode === code.code ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(code.code)} className="text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Referral codes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-casino-gold flex items-center gap-2">
            <Ticket className="w-5 h-5" />
            Códigos de Referencia ({referralCodes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referralCodes.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No hay códigos de referencia</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {referralCodes.map((code) => (
                <div
                  key={code.code}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedCodes.has(code.code)}
                      onChange={() => toggleSelect(code.code)}
                      className="rounded"
                    />
                    <div>
                      <p className="font-mono font-bold">{code.code}</p>
                      <p className="text-xs text-gray-500">
                        Por: {code.createdByName} ({code.createdByRole}) - {formatDate(code.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {code.usedCount > 0 ? (
                      <span className="text-xs bg-zinc-700 text-gray-400 px-2 py-1 rounded">
                        Usado ({code.usedCount})
                      </span>
                    ) : code.active ? (
                      <span className="text-xs bg-green-900/50 text-green-400 px-2 py-1 rounded">Activo</span>
                    ) : (
                      <span className="text-xs bg-red-900/50 text-red-400 px-2 py-1 rounded">Inactivo</span>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleCopy(code.code)}>
                      {copiedCode === code.code ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(code.code)} className="text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
